// Word-level phoneme analysis for pronunciation scoring
// This module analyzes the acoustic features of individual words to score pronunciation clarity

export interface WordPhonemeScore {
  word: string;
  phonemeClarity: number; // 0-100 score for phoneme pronunciation clarity
  articulationScore: number; // How well-articulated the word is
  spectralQuality: number; // Quality of frequency spectrum
  issues: string[]; // Specific pronunciation issues detected
}

export interface PhonemeFeatures {
  formants: { f1: number; f2: number; f3: number };
  spectralCentroid: number;
  spectralSpread: number;
  zeroCrossingRate: number;
  energy: number;
  harmonicity: number;
}

// Common English phoneme patterns and their expected acoustic features
const PHONEME_PATTERNS: Record<string, {
  expectedFormants?: { f1: [number, number]; f2: [number, number]; f3: [number, number] };
  spectralCentroid?: [number, number];
  zeroCrossingRate?: [number, number];
  harmonicity?: [number, number];
}> = {
  // Vowels
  'i': { // as in 'beat'
    expectedFormants: { f1: [200, 400], f2: [2200, 2800], f3: [2900, 3500] },
    harmonicity: [0.7, 1.0]
  },
  'ɪ': { // as in 'bit'
    expectedFormants: { f1: [300, 500], f2: [1800, 2300], f3: [2500, 3100] },
    harmonicity: [0.7, 1.0]
  },
  'e': { // as in 'bait'
    expectedFormants: { f1: [350, 550], f2: [1700, 2200], f3: [2400, 3000] },
    harmonicity: [0.7, 1.0]
  },
  'æ': { // as in 'bat'
    expectedFormants: { f1: [600, 900], f2: [1500, 2000], f3: [2400, 2900] },
    harmonicity: [0.7, 1.0]
  },
  'ɑ': { // as in 'father'
    expectedFormants: { f1: [600, 800], f2: [1000, 1400], f3: [2300, 2800] },
    harmonicity: [0.7, 1.0]
  },
  'u': { // as in 'boot'
    expectedFormants: { f1: [200, 400], f2: [600, 1000], f3: [2100, 2600] },
    harmonicity: [0.7, 1.0]
  },
  
  // Consonants
  's': { // Voiceless fricative
    spectralCentroid: [4000, 8000],
    zeroCrossingRate: [0.4, 0.8],
    harmonicity: [0.0, 0.3]
  },
  'z': { // Voiced fricative
    spectralCentroid: [3000, 7000],
    zeroCrossingRate: [0.3, 0.7],
    harmonicity: [0.2, 0.5]
  },
  'ʃ': { // 'sh' sound
    spectralCentroid: [2000, 4500],
    zeroCrossingRate: [0.3, 0.6],
    harmonicity: [0.0, 0.3]
  },
  'θ': { // 'th' as in 'think'
    spectralCentroid: [1500, 3500],
    zeroCrossingRate: [0.3, 0.6],
    harmonicity: [0.0, 0.2]
  },
  'ð': { // 'th' as in 'the'
    spectralCentroid: [1500, 3500],
    zeroCrossingRate: [0.2, 0.5],
    harmonicity: [0.1, 0.4]
  },
  'r': { // English 'r'
    expectedFormants: { f1: [200, 500], f2: [1000, 1600], f3: [1300, 1900] },
    harmonicity: [0.5, 0.9]
  },
  'l': { // English 'l'
    expectedFormants: { f1: [250, 450], f2: [1000, 1500], f3: [2200, 2800] },
    harmonicity: [0.6, 0.9]
  }
};

// Word to phoneme mapping (simplified - in production would use a proper dictionary)
const WORD_PHONEMES: Record<string, string[]> = {
  // Common practice words
  'she': ['ʃ', 'i'],
  'sells': ['s', 'ɛ', 'l', 'z'],
  'seashells': ['s', 'i', 'ʃ', 'ɛ', 'l', 'z'],
  'whose': ['h', 'u', 'z'],
  'shoes': ['ʃ', 'u', 'z'],
  'choose': ['tʃ', 'u', 'z'],
  'peter': ['p', 'i', 't', 'ər'],
  'piper': ['p', 'aɪ', 'p', 'ər'],
  'picked': ['p', 'ɪ', 'k', 't'],
  'red': ['r', 'ɛ', 'd'],
  'lorry': ['l', 'ɔ', 'r', 'i'],
  'yellow': ['j', 'ɛ', 'l', 'oʊ'],
  'think': ['θ', 'ɪ', 'ŋ', 'k'],
  'this': ['ð', 'ɪ', 's'],
  'that': ['ð', 'æ', 't'],
  'the': ['ð', 'ə'],
  // Add more as needed
};

export class WordPhonemeAnalyzer {
  private sampleRate: number;
  
  constructor(sampleRate: number = 44100) {
    this.sampleRate = sampleRate;
  }
  
  // Analyze a word's pronunciation quality
  analyzeWordPronunciation(
    audioSegment: Float32Array,
    word: string,
    startTime: number,
    endTime: number
  ): WordPhonemeScore {
    // Extract acoustic features
    const features = this.extractPhonemeFeatures(audioSegment);
    
    // Get expected phonemes for the word
    const expectedPhonemes = this.getWordPhonemes(word.toLowerCase());
    
    // Score pronunciation clarity
    const clarityScore = this.scorePhonemeClarity(features, expectedPhonemes);
    
    // Score articulation (how clearly separated phonemes are)
    const articulationScore = this.scoreArticulation(audioSegment);
    
    // Score spectral quality (frequency richness and balance)
    const spectralQuality = this.scoreSpectralQuality(features);
    
    // Detect specific issues
    const issues = this.detectPronunciationIssues(features, expectedPhonemes, clarityScore);
    
    return {
      word,
      phonemeClarity: Math.round(clarityScore),
      articulationScore: Math.round(articulationScore),
      spectralQuality: Math.round(spectralQuality),
      issues
    };
  }
  
  private extractPhonemeFeatures(audioSegment: Float32Array): PhonemeFeatures {
    // Calculate formants using LPC or cepstral analysis (simplified here)
    const formants = this.estimateFormants(audioSegment);
    
    // Calculate spectral centroid (brightness indicator)
    const spectralCentroid = this.calculateSpectralCentroid(audioSegment);
    
    // Calculate spectral spread
    const spectralSpread = this.calculateSpectralSpread(audioSegment, spectralCentroid);
    
    // Calculate zero crossing rate (voiced/unvoiced indicator)
    const zeroCrossingRate = this.calculateZeroCrossingRate(audioSegment);
    
    // Calculate energy
    const energy = this.calculateEnergy(audioSegment);
    
    // Calculate harmonicity (voiced quality)
    const harmonicity = this.calculateHarmonicity(audioSegment);
    
    return {
      formants,
      spectralCentroid,
      spectralSpread,
      zeroCrossingRate,
      energy,
      harmonicity
    };
  }
  
  private estimateFormants(audioSegment: Float32Array): { f1: number; f2: number; f3: number } {
    // Simplified formant estimation using peak detection in spectrum
    const fftSize = 2048;
    const spectrum = this.computeSpectrum(audioSegment, fftSize);
    
    // Find peaks in spectrum (formants appear as peaks)
    const peaks = this.findSpectralPeaks(spectrum, 3);
    
    return {
      f1: peaks[0] || 500,
      f2: peaks[1] || 1500,
      f3: peaks[2] || 2500
    };
  }
  
  private computeSpectrum(signal: Float32Array, fftSize: number): Float32Array {
    // Apply Hamming window
    const windowed = new Float32Array(fftSize);
    for (let i = 0; i < Math.min(signal.length, fftSize); i++) {
      const windowValue = 0.54 - 0.46 * Math.cos(2 * Math.PI * i / (fftSize - 1));
      windowed[i] = signal[i] * windowValue;
    }
    
    // Compute FFT (simplified - in production use proper FFT)
    const spectrum = new Float32Array(fftSize / 2);
    for (let k = 0; k < fftSize / 2; k++) {
      let real = 0;
      let imag = 0;
      
      for (let n = 0; n < fftSize; n++) {
        const angle = -2 * Math.PI * k * n / fftSize;
        real += windowed[n] * Math.cos(angle);
        imag += windowed[n] * Math.sin(angle);
      }
      
      spectrum[k] = Math.sqrt(real * real + imag * imag);
    }
    
    return spectrum;
  }
  
  private findSpectralPeaks(spectrum: Float32Array, numPeaks: number): number[] {
    const peaks: number[] = [];
    const minDistance = 10; // Minimum distance between peaks
    
    for (let i = 1; i < spectrum.length - 1; i++) {
      if (spectrum[i] > spectrum[i - 1] && spectrum[i] > spectrum[i + 1]) {
        const frequency = (i * this.sampleRate) / (spectrum.length * 2);
        
        // Check if far enough from existing peaks
        const farEnough = peaks.every(p => Math.abs(p - frequency) > minDistance);
        
        if (farEnough && frequency > 100 && frequency < 4000) { // Focus on speech range
          peaks.push(frequency);
          
          if (peaks.length >= numPeaks) {
            break;
          }
        }
      }
    }
    
    return peaks.sort((a, b) => a - b);
  }
  
  private calculateSpectralCentroid(audioSegment: Float32Array): number {
    const spectrum = this.computeSpectrum(audioSegment, 1024);
    
    let numerator = 0;
    let denominator = 0;
    
    for (let i = 0; i < spectrum.length; i++) {
      const frequency = (i * this.sampleRate) / (spectrum.length * 2);
      numerator += frequency * spectrum[i];
      denominator += spectrum[i];
    }
    
    return denominator > 0 ? numerator / denominator : 0;
  }
  
  private calculateSpectralSpread(audioSegment: Float32Array, centroid: number): number {
    const spectrum = this.computeSpectrum(audioSegment, 1024);
    
    let numerator = 0;
    let denominator = 0;
    
    for (let i = 0; i < spectrum.length; i++) {
      const frequency = (i * this.sampleRate) / (spectrum.length * 2);
      const deviation = frequency - centroid;
      numerator += deviation * deviation * spectrum[i];
      denominator += spectrum[i];
    }
    
    return denominator > 0 ? Math.sqrt(numerator / denominator) : 0;
  }
  
  private calculateZeroCrossingRate(audioSegment: Float32Array): number {
    let crossings = 0;
    
    for (let i = 1; i < audioSegment.length; i++) {
      if ((audioSegment[i] >= 0) !== (audioSegment[i - 1] >= 0)) {
        crossings++;
      }
    }
    
    return crossings / audioSegment.length;
  }
  
  private calculateEnergy(audioSegment: Float32Array): number {
    let energy = 0;
    
    for (let i = 0; i < audioSegment.length; i++) {
      energy += audioSegment[i] * audioSegment[i];
    }
    
    return energy / audioSegment.length;
  }
  
  private calculateHarmonicity(audioSegment: Float32Array): number {
    // Simplified harmonicity calculation using autocorrelation
    const maxLag = Math.min(500, audioSegment.length / 2);
    let maxCorr = 0;
    
    for (let lag = 20; lag < maxLag; lag++) {
      let correlation = 0;
      let count = 0;
      
      for (let i = 0; i < audioSegment.length - lag; i++) {
        correlation += audioSegment[i] * audioSegment[i + lag];
        count++;
      }
      
      if (count > 0) {
        correlation /= count;
        maxCorr = Math.max(maxCorr, Math.abs(correlation));
      }
    }
    
    return Math.min(1, maxCorr * 10); // Normalize to 0-1
  }
  
  private getWordPhonemes(word: string): string[] {
    // Look up word in dictionary
    if (WORD_PHONEMES[word]) {
      return WORD_PHONEMES[word];
    }
    
    // Fallback: simple heuristic-based phoneme estimation
    const phonemes: string[] = [];
    const vowels = /[aeiou]/i;
    
    for (let i = 0; i < word.length; i++) {
      const char = word[i];
      
      if (vowels.test(char)) {
        // Map to closest vowel phoneme
        switch (char.toLowerCase()) {
          case 'a': phonemes.push('æ'); break;
          case 'e': phonemes.push('ɛ'); break;
          case 'i': phonemes.push('ɪ'); break;
          case 'o': phonemes.push('ɑ'); break;
          case 'u': phonemes.push('u'); break;
        }
      } else {
        // Map consonants
        switch (char.toLowerCase()) {
          case 's': phonemes.push('s'); break;
          case 'z': phonemes.push('z'); break;
          case 'r': phonemes.push('r'); break;
          case 'l': phonemes.push('l'); break;
          case 't': phonemes.push('t'); break;
          case 'd': phonemes.push('d'); break;
          case 'n': phonemes.push('n'); break;
          case 'm': phonemes.push('m'); break;
          case 'p': phonemes.push('p'); break;
          case 'b': phonemes.push('b'); break;
          case 'k': case 'c': phonemes.push('k'); break;
          case 'g': phonemes.push('g'); break;
          case 'f': phonemes.push('f'); break;
          case 'v': phonemes.push('v'); break;
          case 'h': phonemes.push('h'); break;
          case 'w': phonemes.push('w'); break;
          case 'y': phonemes.push('j'); break;
        }
      }
    }
    
    return phonemes;
  }
  
  private scorePhonemeClarity(features: PhonemeFeatures, expectedPhonemes: string[]): number {
    if (expectedPhonemes.length === 0) {
      return 50; // Default score if no phonemes detected
    }
    
    let totalScore = 0;
    let phonemeCount = 0;
    
    // Score each expected phoneme
    for (const phoneme of expectedPhonemes) {
      const pattern = PHONEME_PATTERNS[phoneme];
      
      if (!pattern) {
        totalScore += 50; // Default score for unknown phonemes
        phonemeCount++;
        continue;
      }
      
      let phonemeScore = 100;
      let featureCount = 0;
      
      // Check formants for vowels
      if (pattern.expectedFormants && features.formants) {
        const f1Score = this.scoreInRange(features.formants.f1, pattern.expectedFormants.f1);
        const f2Score = this.scoreInRange(features.formants.f2, pattern.expectedFormants.f2);
        const f3Score = this.scoreInRange(features.formants.f3, pattern.expectedFormants.f3);
        
        phonemeScore = (phonemeScore + f1Score * 2 + f2Score * 2 + f3Score) / 6;
        featureCount++;
      }
      
      // Check spectral centroid for consonants
      if (pattern.spectralCentroid) {
        const centroidScore = this.scoreInRange(features.spectralCentroid, pattern.spectralCentroid);
        phonemeScore = (phonemeScore * featureCount + centroidScore) / (featureCount + 1);
        featureCount++;
      }
      
      // Check zero crossing rate
      if (pattern.zeroCrossingRate) {
        const zcrScore = this.scoreInRange(features.zeroCrossingRate, pattern.zeroCrossingRate);
        phonemeScore = (phonemeScore * featureCount + zcrScore) / (featureCount + 1);
        featureCount++;
      }
      
      // Check harmonicity
      if (pattern.harmonicity) {
        const harmScore = this.scoreInRange(features.harmonicity, pattern.harmonicity);
        phonemeScore = (phonemeScore * featureCount + harmScore) / (featureCount + 1);
        featureCount++;
      }
      
      totalScore += phonemeScore;
      phonemeCount++;
    }
    
    return phonemeCount > 0 ? totalScore / phonemeCount : 50;
  }
  
  private scoreInRange(value: number, range: [number, number]): number {
    const [min, max] = range;
    
    if (value >= min && value <= max) {
      // Perfect score if in range
      return 100;
    }
    
    // Calculate how far off the value is
    const distance = value < min ? min - value : value - max;
    const rangeSize = max - min;
    
    // Penalty based on distance from range
    const penalty = (distance / rangeSize) * 50;
    
    return Math.max(0, 100 - penalty);
  }
  
  private scoreArticulation(audioSegment: Float32Array): number {
    // Measure how well-separated phonemes are (using energy contour)
    const windowSize = Math.floor(audioSegment.length / 20);
    const energyContour: number[] = [];
    
    for (let i = 0; i < audioSegment.length - windowSize; i += windowSize / 2) {
      let windowEnergy = 0;
      
      for (let j = 0; j < windowSize; j++) {
        windowEnergy += audioSegment[i + j] * audioSegment[i + j];
      }
      
      energyContour.push(windowEnergy / windowSize);
    }
    
    // Look for clear transitions (good articulation)
    let transitions = 0;
    const threshold = 0.3 * Math.max(...energyContour);
    
    for (let i = 1; i < energyContour.length; i++) {
      const diff = Math.abs(energyContour[i] - energyContour[i - 1]);
      
      if (diff > threshold) {
        transitions++;
      }
    }
    
    // Score based on number of transitions (more = better articulation)
    const expectedTransitions = Math.max(2, energyContour.length / 5);
    const transitionRatio = transitions / expectedTransitions;
    
    return Math.min(100, transitionRatio * 100);
  }
  
  private scoreSpectralQuality(features: PhonemeFeatures): number {
    // Score based on spectral richness and balance
    let score = 100;
    
    // Penalize if spectral centroid is too low (muffled) or too high (harsh)
    if (features.spectralCentroid < 500) {
      score -= 20; // Too muffled
    } else if (features.spectralCentroid > 4000) {
      score -= 15; // Too harsh
    }
    
    // Good spread indicates rich spectrum
    if (features.spectralSpread < 100) {
      score -= 15; // Too narrow
    } else if (features.spectralSpread > 2000) {
      score -= 10; // Too dispersed
    }
    
    // Energy should be moderate
    if (features.energy < 0.001) {
      score -= 30; // Too quiet
    } else if (features.energy > 0.5) {
      score -= 20; // Too loud/distorted
    }
    
    return Math.max(0, score);
  }
  
  private detectPronunciationIssues(
    features: PhonemeFeatures,
    expectedPhonemes: string[],
    clarityScore: number
  ): string[] {
    const issues: string[] = [];
    
    // Check for common pronunciation problems
    if (features.spectralCentroid < 500) {
      issues.push('Muffled sound - try opening your mouth more');
    }
    
    if (features.energy < 0.001) {
      issues.push('Too quiet - speak louder and clearer');
    }
    
    if (features.zeroCrossingRate > 0.7 && expectedPhonemes.some(p => ['s', 'z', 'ʃ'].includes(p))) {
      issues.push('Sibilant sounds may be too sharp');
    }
    
    if (clarityScore < 50) {
      issues.push('Phonemes not clearly pronounced');
    }
    
    if (features.harmonicity < 0.3 && expectedPhonemes.some(p => ['a', 'e', 'i', 'o', 'u'].includes(p))) {
      issues.push('Vowels lack clarity - try sustaining them longer');
    }
    
    // Check for specific phoneme issues
    const hasRSound = expectedPhonemes.includes('r');
    const hasLSound = expectedPhonemes.includes('l');
    const hasThSound = expectedPhonemes.includes('θ') || expectedPhonemes.includes('ð');
    
    if (hasRSound && features.formants.f3 < 1500) {
      issues.push('R sound may not be clear - curl tongue more');
    }
    
    if (hasLSound && features.formants.f2 > 1800) {
      issues.push('L sound unclear - touch tongue to roof of mouth');
    }
    
    if (hasThSound && features.spectralCentroid > 4000) {
      issues.push('TH sound too sharp - place tongue between teeth');
    }
    
    return issues;
  }
}